import axios from "axios";
import { getEnvironment } from "../utils/getEnvironment";

const environment = getEnvironment();

interface OriginalField {
    text: string | null;
    lang: string;
}

interface ServiceRaw {
    key: string;
    category: string;
    name: string;
    duration: number;
    price: number;
    gallery: boolean;
    description: string;
    reschedule: boolean;
    enabled: boolean;
    position: number | null;
    image?: string | null;
    currency?: string;
    clientKey?: string;
}

// what /service/list returns
interface ServiceRawList extends ServiceRaw {
    nameOriginal: OriginalField;
    descriptionOriginal: OriginalField;
}

export default class Service {
    key: string;
    category: string;
    name: string;
    nameOriginal?: OriginalField | null;
    duration: number;
    price: number;
    image?: string | null;
    description: string;
    descriptionOriginal?: OriginalField | null;
    enabled: boolean;
    position: number | null;
    gallery: boolean;
    reschedule: boolean;
    clientKey?: string;
    currency?: string;

    constructor(data: ServiceRaw | ServiceRawList) {
        this.key = data.key;
        this.category = data.category;
        this.name = data.name;
        this.duration = data.duration;
        this.price = data.price;
        this.gallery = data.gallery;
        this.description = data.description;
        this.reschedule = data.reschedule;
        this.enabled = data.enabled;
        this.position = data.position;
        this.image = data.image;
        this.clientKey = data.clientKey;
        this.currency = data.currency;
        this.nameOriginal = (data as any).nameOriginal ?? null;
        this.descriptionOriginal = (data as any).descriptionOriginal ?? null;
    }

    public static readonly list = async (
        uuid: string,
        brand: string,
        category: string,
        duration?: number,
        sortedByPosition: boolean = false,
        opts?: { includeOriginal?: boolean }
    ) =>
        (
            (await axios
                .get(
                    environment.api_url +
                        "/" +
                        brand +
                        "/category/" +
                        category +
                        "/service/list",
                    {
                        headers: {
                            "User-Token": uuid,
                        },
                        params: duration ? { duration } : undefined,
                    }
                )
                .then((res) => (res.status === 200 ? res.data : undefined))
                .catch(console.error)) as ServiceRawList[] | undefined
        )
            ?.map((x) => new Service(x))
            ?.sort((a: Service, b: Service) =>
                sortedByPosition
                    ? (a.position ?? Infinity) - (b.position ?? Infinity)
                    : 0
            );

    public static readonly info = async (
        uuid: string,
        brand: string,
        service: string,
        category?: string
    ) => {
        const data = (await axios
            .get(
                environment.api_url +
                    "/" +
                    brand +
                    (category ? "/category/" + category : "") +
                    "/service/" +
                    service,
                {
                    headers: {
                        "User-Token": uuid,
                    },
                }
            )
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error)) as ServiceRaw | undefined;
        if (data) return new Service(data);
        return undefined;
    };

    public static readonly register = async (
        uuid: string,
        brand: string,
        category: string,
        name: string,
        duration: number,
        price: number,
        gallery: boolean,
        description: string,
        reschedule: boolean,
        enabled: boolean,
        image?: string | null
    ) => {
        return (
            await axios
                .post(
                    environment.api_url +
                        "/" +
                        brand +
                        "/category/" +
                        category +
                        "/service",
                    {
                        name,
                        duration,
                        price,
                        gallery,
                        description,
                        reschedule,
                        enabled,
                        image,
                    },
                    {
                        headers: {
                            "User-Token": uuid,
                        },
                    }
                )
                .then((res) => res.data)
                .catch(console.error)
        )?.key as string | undefined;
    };

    public static readonly update = async (
        uuid: string,
        brand: string,
        category: string,
        service: string,
        name: string,
        duration: number,
        price: number,
        gallery: boolean,
        description: string,
        reschedule: boolean,
        enabled: boolean,
        position: number | null,
        originalLang: string,
        image?: string | null
    ) => {
        return axios.put(
            environment.api_url +
                "/" +
                brand +
                "/category/" +
                category +
                "/service/" +
                service,
            {
                name,
                duration,
                price,
                gallery,
                description,
                reschedule,
                enabled,
                position,
                originalLang,
                image,
            },
            {
                headers: {
                    "User-Token": uuid,
                },
            }
        );
    };

    public static readonly delete = async (
        uuid: string,
        brand: string,
        category: string,
        service: string
    ) => {
        try {
            await axios.delete(
                `${environment.api_url}/${brand}/category/${category}/service/${service}`,
                {
                    headers: {
                        "User-Token": uuid,
                    },
                }
            );
            return true; // Indicates success
        } catch (error) {
            console.error("Failed to delete service:", error);
            return false; // Indicates failure
        }
    };

    public static readonly updatePosition = async (
        uuid: string,
        brand: string,
        category: string,
        service: string,
        enabled: boolean,
        position: number | null
    ) => {
        return axios.put(
            environment.api_url +
                "/" +
                brand +
                "/category/" +
                category +
                "/service/" +
                service,
            {
                enabled,
                position,
            },
            {
                headers: {
                    "User-Token": uuid,
                },
            }
        );
    };
}
